import React, { useEffect, useState } from "react";
import {
  RuntimeSettings,
  getSettings,
  saveSettings,
} from "../services/settingsApi";
import "./UsersAdmin.css";
import "./SettingsAdmin.css";

/**
 * Runtime usage-limit settings, super-admin only. Rain (service tokens) and
 * super admins are exempt from every limit at the server, so nothing here
 * can affect them - the note below says so on-screen.
 */

type LimitField = keyof RuntimeSettings["limits"]["user"];

const LIMIT_ROWS: { field: LimitField; label: string; help: string }[] = [
  {
    field: "requestsPerMinute",
    label: "Requests per minute",
    help: "Any API call counts toward this. Measured over a sliding one-minute window per user; calls beyond the limit are rejected until the window clears.",
  },
  {
    field: "decisionsPerDay",
    label: "Decisions per day",
    help: "Full dispute decisions per day. This is the expensive call - every decision runs multiple AI models.",
  },
  {
    field: "evidencePerDay",
    label: "Evidence analyses per day",
    help: "Individual evidence items (files and links) analyzed per day, counted across all of the user's requests.",
  },
  {
    field: "itemsPerRequest",
    label: "Evidence items per request",
    help: "Maximum number of evidence items allowed in a single analysis request. Requests with more items are rejected up front.",
  },
];

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="info-tip" tabIndex={0}>
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 11v5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="8" r="1.2" fill="currentColor" />
    </svg>
    <span className="info-tip-bubble" role="tooltip">
      {text}
    </span>
  </span>
);

const SettingsAdmin: React.FC = () => {
  const [settings, setSettings] = useState<RuntimeSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        setSettings(data.settings);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || err?.message || "Failed to load"
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const setLimit = (
    role: "user" | "admin",
    field: LimitField,
    value: number
  ) => {
    if (!settings) return;
    setSettings({
      ...settings,
      limits: {
        ...settings.limits,
        [role]: { ...settings.limits[role], [field]: value },
      },
    });
    setSavedAt(null);
  };

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await saveSettings({
        limits: settings.limits,
        auth: settings.auth,
        processing: settings.processing,
      });
      setSettings(updated);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err: any) {
      const data = err?.response?.data;
      setError(
        data?.errors?.join("; ") || data?.message || err?.message || "Save failed"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="settings-admin"><p className="settings-note">Loading…</p></div>;
  }
  if (!settings) {
    return (
      <div className="settings-admin">
        <div className="users-admin-error">{error || "Failed to load settings"}</div>
      </div>
    );
  }

  return (
    <div className="settings-admin">
      <div className="users-admin-header">
        <h1>Usage Limits</h1>
        <p className="users-admin-subtitle">
          Changes take effect within seconds - no restart needed. Rain
          (service tokens) and super admins are always exempt.
        </p>
      </div>

      {error && <div className="users-admin-error">{error}</div>}

      <div className="settings-card settings-limits-card">
        <div className="settings-card-head">
          <h2>Spending limits by role</h2>
          <p>
            Per-user quotas on the endpoints that cost money. Hover an info
            icon for what each limit covers.
          </p>
        </div>
        <div className="settings-table-wrap">
        <table className="settings-table">
          <thead>
            <tr>
              <th className="settings-th-limit">Limit</th>
              <th>Regular users</th>
              <th>Admins</th>
            </tr>
          </thead>
          <tbody>
            {LIMIT_ROWS.map(({ field, label, help }) => (
              <tr key={field}>
                <th scope="row">
                  <span className="settings-row-label">
                    {label}
                    <InfoTip text={help} />
                  </span>
                </th>
                {(["user", "admin"] as const).map((role) => (
                  <td key={role}>
                    <input
                      type="number"
                      min={1}
                      value={settings.limits[role][field]}
                      onChange={(e) =>
                        setLimit(role, field, parseInt(e.target.value) || 0)
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <div className="settings-card-head">
            <h2>Sign-in protection</h2>
            <p>Per-IP throttles that slow down password guessing and bulk signups.</p>
          </div>
          <label className="settings-field">
            <span className="settings-row-label">
              Login attempts per minute
              <InfoTip text="Sign-in attempts allowed per minute from a single IP address, successful or not. Attempts beyond the limit are rejected." />
            </span>
            <input
              type="number"
              min={1}
              value={settings.auth.loginPerMinutePerIp}
              onChange={(e) => {
                setSettings({
                  ...settings,
                  auth: {
                    ...settings.auth,
                    loginPerMinutePerIp: parseInt(e.target.value) || 0,
                  },
                });
                setSavedAt(null);
              }}
            />
          </label>
          <label className="settings-field">
            <span className="settings-row-label">
              Signups per hour
              <InfoTip text="New account registrations allowed per hour from a single IP address. New accounts still wait for approval either way." />
            </span>
            <input
              type="number"
              min={1}
              value={settings.auth.signupPerHourPerIp}
              onChange={(e) => {
                setSettings({
                  ...settings,
                  auth: {
                    ...settings.auth,
                    signupPerHourPerIp: parseInt(e.target.value) || 0,
                  },
                });
                setSavedAt(null);
              }}
            />
          </label>
        </div>

        <div className="settings-card">
          <div className="settings-card-head">
            <h2>Processing</h2>
            <p>Server-side behavior - applies to every request, all roles.</p>
          </div>
          <label className="settings-field">
            <span className="settings-row-label">
              Parallel evidence analyses
              <InfoTip text="How many evidence items the server analyzes at the same time within one request. Higher finishes sooner but puts more simultaneous load on the AI providers." />
            </span>
            <input
              type="number"
              min={1}
              value={settings.processing.evidenceConcurrency}
              onChange={(e) => {
                setSettings({
                  ...settings,
                  processing: {
                    evidenceConcurrency: parseInt(e.target.value) || 0,
                  },
                });
                setSavedAt(null);
              }}
            />
          </label>
        </div>
      </div>

      <div className="settings-actions">
        <button
          className="settings-save"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save changes"}
        </button>
        <div className="settings-actions-meta">
          {savedAt && <span className="settings-saved">Saved at {savedAt}</span>}
          {settings.updatedAt && (
            <span className="settings-note">
              Last changed {new Date(settings.updatedAt).toLocaleString()}
              {settings.updatedBy?.email ? ` by ${settings.updatedBy.email}` : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsAdmin;
